import axios from 'axios';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { buildInsertionPrompt, washersOn, type InsertionContext } from './insertionPrompt';
import { storedId } from './partsCatalogue';
import { ensureCategories } from './catalogCategories';
import { haltGate } from './runHalt';
import { renderPartViews } from './partViews';
import type { Job3D } from './threeDApi';

// THE INSERTION TABLE — how each piece of hardware or cover goes into its hole: one call over all
// of them, with two pictures of each part's 3D model (see insertionPrompt), answered per part. What drilling reads a hole's width
// and a fastener's reach off (see holeWidthOf, hardwareReachOf).

// ONE PART'S ANSWER. `axis` is its 3D model's; the three figures in mm, each null where the call
// gave none — a hole's width is null on a part that goes into no hole.
export type Insertion = {
  axis: 'x' | 'y' | 'z';
  holeDiameterMm: number | null;
  // How much of it, along the axis, ends up inside the parts — all it goes into, together — and outside them.
  insideMm: number | null;
  outsideMm: number | null;
};

type Snapshot = {
  busy: boolean;
  // By stored part id.
  byPart: Record<string, Insertion>;
  // Whether the call has ever answered for this manual.
  asked: boolean;
  reasoning: string;
  raw: string;
  error: string | null;
  startedAt: number | null;
  elapsed: number | null;
};

const key = (docId: string) => `notice.hardwareInsertion.${docId}`;
type Stored = { byPart?: Record<string, Insertion>; reasoning?: string; raw?: string; elapsed?: number | null };

const load = (docId: string): Snapshot => {
  const stored = readState<Stored | null>(docId, key(docId), null);
  return {
    busy: false,
    byPart: stored?.byPart ?? {},
    asked: !!stored,
    reasoning: stored?.reasoning ?? '',
    raw: stored?.raw ?? '',
    error: null,
    startedAt: null,
    elapsed: stored?.elapsed ?? null,
  };
};

const store = createDocEntryStore<Snapshot>((docId) => ({ snapshot: load(docId) }));

export const useInsertion = store.useSnapshot;
export const getInsertion = store.get;

// THE PICTURES THE CALL IS SHOWN: each part's 3D model in the Parts DB, from the front and from
// above inside its measured box — the two Phase 7's movement call reads a part's axis off (see
// partViews). A part with no model, or one that will not render, is described in words alone.
// Returns the context with `views` set, and the pictures in that order.
export async function insertionShots(
  ctx: InsertionContext, jobOf: (id: string) => Job3D | null,
): Promise<{ ctx: InsertionContext; images: string[] }> {
  const views: string[] = [];
  const images: string[] = [];
  // EACH PART, AND THE WASHERS THE JOINTS TABLE PUTS ON IT: not asked about, but what they hold
  // out of the wood is read off them too.
  for (const id of ctx.ids.flatMap((one) => [one, ...washersOn(ctx.table, one)])) {
    const job = jobOf(id);
    if (!job) continue;
    try {
      const made = await renderPartViews(job, ctx.items[Number(id)]?.size, ['front', 'top']);
      if (made.length < 2) continue;
      views.push(id);
      images.push(...made.map((v) => v.dataUrl));
    } catch {
      // Described in words alone — not a reason to fail the call.
    }
  }
  return { ctx: { ...ctx, views }, images };
}

// Run the call. Replaces the table when it answers; leaves it standing when it fails.
export async function findInsertion(
  docId: string, base: InsertionContext, jobOf: (id: string) => Job3D | null, model: string, t: (k: string) => string,
) {
  if (store.get(docId).busy) return;
  if (!model || !base.ids.length) {
    store.patch(docId, { error: t('notice.hardwareAxis.noHardware') });
    return;
  }
  // The ontology before the prompt: until it lands no part reads as hardware.
  await ensureCategories();
  const halted = haltGate(docId);
  const startedAt = Date.now();
  store.patch(docId, { busy: true, error: null, startedAt, elapsed: null });
  try {
    const { ctx, images } = await insertionShots(base, jobOf);
    if (halted()) return;
    const { data } = await axios.post<{
      parts?: ({ id: string } & Insertion)[]; reasoning?: string; raw?: string;
    }>('/api/notice/hardware-insertion', { model, prompt: buildInsertionPrompt(ctx), images });
    if (halted()) return;
    // THE IDS COME BACK ONE HIGHER THAN THEY ARE STORED (see shownId), and only asked ones are kept.
    const byPart: Record<string, Insertion> = {};
    for (const one of data.parts ?? []) {
      const id = storedId(String(one.id), ctx.items.length);
      if (!ctx.ids.includes(id)) continue;
      byPart[id] = { axis: one.axis, holeDiameterMm: one.holeDiameterMm, insideMm: one.insideMm, outsideMm: one.outsideMm };
    }
    const elapsed = Math.round((Date.now() - startedAt) / 1000);
    const reasoning = data.reasoning || '';
    const raw = data.raw || '';
    writeState(docId, key(docId), { byPart, reasoning, raw, elapsed });
    store.patch(docId, { byPart, asked: true, reasoning, raw });
  } catch (e) {
    const msg = (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error)
      || (e instanceof Error && e.message) || t('notice.err.generic');
    store.patch(docId, { error: msg });
  } finally {
    store.patch(docId, { busy: false, startedAt: null, elapsed: Math.round((Date.now() - startedAt) / 1000) });
  }
}
